// Imports
import AxiosConfig from './serverAxiosConfig'
import firebase from '../config/FirebaseConfig'
import { createQuery } from '../../utils/filterUtils'

// Fetch AD
const getBoardAds = async (boardId, nextId, filters) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const params = {
    // Create the filters
    ...createQuery(filters),
    next: nextId
  }

  const { data } = await AxiosConfig.get(
    `/ads/board/${boardId}`,
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getBoardsAdCount = async (teamId) => {
  const user = firebase.auth().currentUser

  // If no user, don't query for ad count of a board
  if (!user) {
    return
  }
  const token = user.Aa

  const payload = {}
  if (teamId) {
    payload.teamId = teamId
  }

  const { data } = await AxiosConfig.post(
    '/ads/board/count',
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )
  return data.count ? data.count : 0
}

const getSharedBoardsAdCount = async (boardId) => {
  const { data } = await AxiosConfig.post(
    '/ads/share/board/count',
    { boardId }
  )
  return data.count ? data.count : 0
}

const getPublicBoardAds = async (boardId, nextId, filters) => {
  const params = {
    // Create the filters
    ...createQuery(filters),
    next: nextId
  }

  const { data } = await AxiosConfig.get(
    `/ads/shared/board/${boardId}`,
    {
      params
    }
  )

  return data
}

const getLibraryAds = async (nextId, filters) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const params = {
    // Create the filters
    ...createQuery(filters),
    next: nextId
  }

  const { data } = await AxiosConfig.get(
    '/ads/library',
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )
  return data
}

const getDiscoveryAds = async (nextId, filters, includeSpyder = false, adsCancelToken = null) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const params = includeSpyder
    ? { ...createQuery(filters), spyder: true, next: nextId }
    : { ...createQuery(filters), next: nextId }

  const { data } = await AxiosConfig.get(
    '/ads/discovery',
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      },
      cancelToken: adsCancelToken
    }
  )

  return data
}

const getShuffledDiscoveryAds = async (nextId, filters) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const params = {
    // Create the filters
    ...createQuery(filters),
    next: nextId
  }

  const { data } = await AxiosConfig.get(
    '/ads/shuffledDiscovery',
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getSpyderAds = async (nextId, filters) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const params = {
    // Create the filters
    ...createQuery(filters),
    spyder: true,
    next: nextId
  }

  const { data } = await AxiosConfig.get(
    '/ads/discovery',
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getSpyderPartnershipAds = async (pageId, nextId, filters) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  // Extract the custom partner creators filter from the filters object
  const { 'or.partner_creators': partnerPageIds, ...otherFilters } = filters

  const params = {
    // Create the filters
    pageId,
    ...(partnerPageIds?.length ? { partnerPageIds } : {}),
    ...createQuery(otherFilters),
    next: nextId
  }
  
  const { data } = await AxiosConfig.get(
    '/ads/spyder/partner-ads',
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

// Helper for hook fetch. Get the most recent ad by startedRunning date.
const newestByStart = (ads) => {
  if (!Array.isArray(ads) || !ads.length) return null
  return ads.reduce((newest, ad) => {
    const t = +new Date(ad?.startedRunning)
    if (!Number.isFinite(t)) return newest
    const best = newest ? +new Date(newest.startedRunning) : -Infinity
    return t > best ? ad : newest
  }, null)
}

// Map results of hook fetch to expected shape
const mapHookGroups = (data) => {
  const results = (data?.results || []).map((group) => ({
    hookKey: group.key,
    displayHook: group.displayHook,
    activeAds: group.activeAds || 0,
    inactiveAds: group.inactiveAds || 0,
    totalAds: group.totalAds ?? (group.activeAds || 0) + (group.inactiveAds || 0),
    longestRunningAd: group.longestRunningAd || null
  }))

  return { ...data, results }
}

// Fetch hook groups for a brand.
// Default is brandId only — pass `opts` (sort, hookKey, size, textSearch, ranBetweenStart/End, etc.)
// ONLY when the user has actually applied a filter. Do not hardcode defaults like
// `transciptionOnly` or `'or.status': ['live']` here.
const getBrandHooks = async (brandId, opts = {}) => {
  const { Aa: token } = firebase.auth().currentUser

  const params = { brandId }
  if (opts && Object.keys(opts).length) {
    Object.assign(params, createQuery(opts))
  }

  const { data } = await AxiosConfig.get(
    '/brands/hooks',
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return mapHookGroups(data)
}

const getBrandPinnedHooks = async (brandId, opts = {}) => {
  const { Aa: token } = firebase.auth().currentUser

  const params = { brandId }
  if (opts && Object.keys(opts).length) {
    Object.assign(params, createQuery(opts))
  }

  const { data } = await AxiosConfig.get(
    '/brands/pinned-hooks',
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return mapHookGroups(data)
}

// Fetch the ads  inside one hook group.
const getHookGroupAds = async (brandId, hookKey, opts = {}) => {
  const { Aa: token } = firebase.auth().currentUser

  const { size, next, ...filterOpts } = opts
  const params = { brandId, hookKey }
  if (filterOpts && Object.keys(filterOpts).length) {
    Object.assign(params, createQuery(filterOpts))
  }
  if (size != null) params.size = size
  if (next != null) params.next = next

  const { data } = await AxiosConfig.get(
    '/brands/hooks/group-ads',
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getSpyderFeedAds = async (nextId, filters, folderIds = []) => {
  const token = await firebase.auth().currentUser.getIdToken()

  const params = {
    ...createQuery(filters),
    next: nextId,
    ...(folderIds.length ? { folderIds } : {})
  }

  const { data } = await AxiosConfig.get(
    '/ads/spyder/feed',
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const setSpyderAdDiscoverable = async (adId) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await AxiosConfig.post(
    '/ads/set-discoverable',
    {
      adId
    },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getExpertAds = async (expertId, nextId, filters) => {
  const body = {
    expertUTM: expertId,
    query: {
      ...createQuery(filters),
      next: nextId
    }
  }

  const { data } = await AxiosConfig.post(
    '/ads/expert',
    body
  )

  return data
}

const createTranscriptionJob = async (adId, manualUpload = false) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await AxiosConfig.post(
    '/transcriptions/ads',
    {
      adId,
      manualUpload
    },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getTagAdCounts = async () => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await AxiosConfig.get(
    '/ads/tags/count',
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data.count ? data.count : 0
}

const createBoardExportJob = async (boardId) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data, status } = await AxiosConfig.post(
    `/ads/board/${boardId}/export`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return { data, status }
}

const getBoardExportStatus = async (boardId) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data, status } = await AxiosConfig.get(
    `/ads/board/${boardId}/export`,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return { data, status }
}

const refreshAdTranscript = async (docId, adId) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await AxiosConfig.post(
    `/ads/transcript/${docId}/refresh`,
    {
      adId
    },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getUniqueAdById = async (uniqueAdId) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await AxiosConfig.get(`/ads/unique/${uniqueAdId}`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })

  return data
}

const getUniqueAdsByIds = async (uniqueAdIds) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await AxiosConfig.get(
    '/ads/discovery/batch',
    {
      params: {
        adIds: uniqueAdIds
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getSharedAd = async (adId, sharedByQuery) => {
  const { data } = await AxiosConfig.get(`/ads/public/${adId}?user=${sharedByQuery}`)

  return data
}

const getPublicAdComments = async (adId) => {
  const { data } = await AxiosConfig.get(`/ads/public/${adId}/comments`)

  return data.data
}

const save = async (payload, isSpyderView = false) => {
  const { Aa: token } = firebase.auth().currentUser
  
  const { data } = await AxiosConfig.post(
    '/fs/ads/save',
    { ...payload, isSpyderView },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )
  
  return data
}

export default {
  getBoardAds,
  getBoardsAdCount,
  getDiscoveryAds,
  getExpertAds,
  getLibraryAds,
  getPublicBoardAds,
  createTranscriptionJob,
  getSpyderAds,
  getSpyderPartnershipAds,
  getBrandHooks,
  getBrandPinnedHooks,
  getHookGroupAds,
  setSpyderAdDiscoverable,
  getUniqueAdById,
  getUniqueAdsByIds,
  getTagAdCounts,
  getShuffledDiscoveryAds,
  createBoardExportJob,
  getBoardExportStatus,
  getSharedBoardsAdCount,
  refreshAdTranscript,
  getSpyderFeedAds,
  save,

  // recently migrated
  getSharedAd,
  getPublicAdComments
}
